import { useMutation, useQuery } from '@tanstack/react-query';
import isNil from 'lodash-es/isNil';
import type { UseMutationResult, UseQueryOptions } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query/src/types';
import type { IntlShape } from 'react-intl/src/types';
import { useSLTConfig } from '../slt/config/SLTConfigProvider';
import { SFCC_PIPELINE_BASE, sfccGet, sfccPost } from './sitegenesis';
import { argsToQueryString, getOriginUrl } from './url-utils';
import { isServer } from './utils';
import type { ContentSlotID } from './api/ContentSlotID';
import type { CustomPreference } from './api/CustomPreference';
import type { HttpParams } from './url-utils';

export enum QueryKey {
    BASKET_COUNT = 'basket-count',
    CUSTOM_PREFERENCE = 'custom-preference',
    CUSTOM_PREFERENCES = 'custom-preferences',
    GET_PRODUCTS = 'get-products',
    GET_CATEGORIES = 'get-categories',
    GET_NEAREST_STORE = 'get-nearest-store',
    GET_NEAREST_STORE_BY_LOCATION = 'get-nearest-store-by-location',
    GET_COOKING_CLASSES_BY_STORE = 'get-cooking-classes-by-store',
    LOCAL_STORAGE = 'local-storage',
    APPLE_PAY_REQUEST = 'apple-pay-request',
    GET_CONTENT_SLOT = 'get-content-slot',
    EMAIL_SIGN_UP_OFFER_EXCLUSIONS = 'footer-subscription-offer-exclusions',
    URL_UTILS = 'url-utils',
    EXPERIENCE_LINKS = 'experience-links',
    ABOUT_LINKS = 'about-links',
    CUSTOMER_SERVICE_LINKS = 'customer-service-links',
    USER_ADDRESS = 'user-address',
    USER_ADDRESS_ZIP_CODE = 'user-address-zip-code',
    CUSTOMER_GEO_LOCATION = 'customer-geo-location',
}

type GetCustomPreferencesResponse = Record<string, string | boolean | number | null>;

const TEN_MINUTES = 10 * 60 * 1000;

export const useCustomPreferences = (
    preferences: CustomPreference[],
): GetCustomPreferencesResponse => {
    const sltConfig = useSLTConfig();
    const preferencesSorted = preferences.sort();
    const initialData = preferencesSorted.every(
        (p) => typeof sltConfig.customPreferences?.[p] !== 'undefined',
    )
        ? sltConfig.customPreferences
        : undefined;
    const query = useQuery<GetCustomPreferencesResponse>({
        queryKey: [QueryKey.CUSTOM_PREFERENCES, ...preferencesSorted],
        queryFn: () =>
            initialData ??
            sfccGet<GetCustomPreferencesResponse>('PwaCustomPreference-GetMultiple', [
                { preferences: preferences.join(',') },
            ]),
        initialData,
        /**
         * - This will contain the value from the server
         * - the 10 minutes of stale time is to prevent the query from being refetched so shortly
         *  after the initial fetch
         */
        staleTime: initialData ? Infinity : TEN_MINUTES,
    });
    return query.data ?? {};
};

export const useCustomPreferenceOrDefault = <T>(
    preference: CustomPreference,
    defaultValue: T,
    options?: Partial<UseQueryOptions<T>>,
): T => {
    const sltConfig = useSLTConfig();
    const initialData = (sltConfig.customPreferences?.[preference] as T) ?? undefined;
    const query = useQuery<T>({
        queryKey: [QueryKey.CUSTOM_PREFERENCE, preference],
        queryFn: () => initialData ?? sfccGet<T>('PwaCustomPreference-Get', [{ preference }]),
        initialData,
        /**
         * - This will contain the value from the server
         * - the 10 minutes of stale time is to prevent the query from being refetched so shortly
         *  after the initial fetch
         */
        staleTime: initialData ? Infinity : TEN_MINUTES,
        keepPreviousData: true,
        ...options,
    });
    return isNil(query.data) ? defaultValue : query.data;
};

export interface RawUrl {
    action: string;
    args?: string[];
}

export const rawUrlHash = (rawUrl?: RawUrl): string => {
    const base: RawUrl = {
        action: rawUrl?.action ?? '',
        args: rawUrl?.args ?? [],
    };
    return JSON.stringify(base);
};

export const useUrl = (action: string, args: string[] = []): string => {
    const sltConfig = useSLTConfig();
    const urlRequest: RawUrl = { action, args };
    const urlKey = rawUrlHash(urlRequest);
    const initialData = sltConfig.urlMap[urlKey];
    const query = useQuery<string>({
        queryKey: [action, ...args],
        queryFn: () => initialData ?? sfccPost<string>('PwaUtils-Url', { action, args }),
        initialData,
        keepPreviousData: true,
        staleTime: initialData ? Infinity : undefined, // This will contain the value from the server
    });
    return (
        query.data ?? `${getOriginUrl()}${SFCC_PIPELINE_BASE}/${action}?${argsToQueryString(args)}`
    );
};

export const useContentAsset = (contentAssetId: string): string => {
    const sltConfig = useSLTConfig();
    const contentAsset = sltConfig.contentAssets[contentAssetId];
    const initialData = contentAsset ? { contentAsset } : undefined;
    const query = useQuery<{ contentAsset: string }>({
        queryKey: ['content', contentAssetId],
        keepPreviousData: true,
        initialData,
        staleTime: initialData ? Infinity : undefined,
        enabled: !!contentAssetId,
        queryFn: () =>
            initialData ??
            sfccGet<{ contentAsset: string }>('PwaContent-GetContentAsset', [
                {
                    contentAssetId,
                },
            ]),
    });
    return typeof query.data?.contentAsset === 'string' ? query.data.contentAsset : '';
};

export const useGetContentSlot = (contentSlotId: ContentSlotID): UseQueryResult<string> => {
    const sltConfig = useSLTConfig();
    const initialData = sltConfig.contentSlots[contentSlotId];
    return useQuery<string>({
        queryKey: [QueryKey.GET_CONTENT_SLOT, contentSlotId],
        queryFn: () =>
            initialData ||
            sfccGet<Response>(
                'PwaContent-GetContentSlot',
                [
                    {
                        contentSlotId,
                    },
                ],
                null,
                false,
                true,
            ).then((r: Response): Promise<string> => (r.ok ? r.text() : Promise.resolve(''))),
        keepPreviousData: true,
        initialData,
        staleTime: initialData ? Infinity : undefined,
    });
};

export const useLogout = (): UseMutationResult<unknown, unknown, unknown> =>
    useMutation({
        mutationFn: async () => sfccGet('PwaAuth-Logout'),
    });

export interface SFCCApplePayRequest {
    request: ApplePayJS.ApplePayPaymentRequest;
}

export const useAppleRequest = (
    enabled: boolean,
    params: Record<string, string | number | boolean>,
): UseQueryResult<SFCCApplePayRequest> =>
    useQuery<SFCCApplePayRequest>({
        queryFn: async () =>
            sfccGet<SFCCApplePayRequest>('__SYSTEM__ApplePay-GetRequest', [params]),
        queryKey: [QueryKey.APPLE_PAY_REQUEST, JSON.stringify(params)],
        enabled: enabled && !isServer,
        keepPreviousData: true,
        staleTime: 3000,
    });

export const placeOrderMessage = (intl: IntlShape, message: string, defaultEmpty = false): string =>
    ({
        OUT_OF_STOCK: intl.formatMessage({
            id: 'order_review.error.availability_error',
            defaultMessage: 'Some products went out of stock',
        }),
        EDIT_BAG: intl.formatMessage({
            id: 'place_order.message.error_placing_order.EDIT_BAG',
            defaultMessage: 'Edit Bag',
        }),
        EDIT_SHIPPING: intl.formatMessage({
            id: 'place_order.message.error_placing_order.EDIT_SHIPPING',
            defaultMessage: 'Edit Shipping',
        }),
        EDIT_BILLING: intl.formatMessage({
            id: 'place_order.message.error_placing_order.EDIT_BILLING',
            defaultMessage: 'Edit Billing',
        }),
        EMPTY_CART: intl.formatMessage({
            id: 'place_order.message.error_placing_order.EMPTY_CART',
            defaultMessage: 'The bag is empty',
        }),
        PAYPAL_INVALID_SHIPPING: intl.formatMessage({
            id: 'place_order.message.error_placing_order.PAYPAL_INVALID_SHIPPING',
            defaultMessage: 'Invalid shipping info for paypal',
        }),
        EMPTY_ADDRESS: intl.formatMessage({
            id: 'place_order.message.error_placing_order.EMPTY_ADDRESS',
            defaultMessage: 'The shipping address is empty',
        }),
        INVALID_ADDRESS: intl.formatMessage({
            id: 'place_order.message.error_placing_order.INVALID_ADDRESS',
            defaultMessage: 'The shipping address is invalid',
        }),
        NON_FULFILLED_BILLING: intl.formatMessage({
            id: 'place_order.message.error_placing_order.NON_FULFILLED_BILLING',
            defaultMessage: 'The billing step is not complete',
        }),
        INVALID_PAYMENT: intl.formatMessage({
            id: 'place_order.message.error_placing_order.INVALID_PAYMENT',
            defaultMessage: 'The payment method validation failed',
        }),
        INVALID_COUPON: intl.formatMessage({
            id: 'place_order.message.error_placing_order.INVALID_COUPON',
            defaultMessage: 'One promo code you entered is not valid.',
        }),
        DECLINED: intl.formatMessage({
            id: 'place_order.message.error_placing_order.DECLINED',
            defaultMessage:
                'Your payment could not be processed. Please review your billing and shipping information and try again. If you have further questions, please contact us.',
        }),
        TECHNICAL: intl.formatMessage({
            id: 'place_order.message.error_placing_order.TECHNICAL',
            defaultMessage:
                "We're sorry that your order could not be placed. This may be a system error, please wait a few minutes and resubmit your order. If you are still experiencing this error, please contact us.",
        }),
        MISSING_PAYMENT_INFO: intl.formatMessage({
            id: 'place_order.message.error_placing_order.MISSING_PAYMENT_INFO',
            defaultMessage: 'Missing payment info',
        }),
        CART_ERROR: intl.formatMessage({
            id: 'place_order.message.error_placing_order.CART_ERROR',
            defaultMessage: 'The bag is not matching the created order',
        }),
        PAYMENT_VALIDATION_NOT_COMPLETED: intl.formatMessage({
            id: 'place_order.message.error_placing_order.PAYMENT_VALIDATION_NOT_COMPLETED',
            defaultMessage: 'The payment method validation was not completed',
        }),
        BOPIS_ORDER_SUBMIT_ERROR: intl.formatMessage({
            id: 'place_order.message.error_placing_order.BOPIS_ORDER_SUBMIT_ERROR',
            defaultMessage:
                "We're sorry that your order could not be placed. This may be a system error, please wait a few minutes and resubmit your order. If you are still experiencing this error, please contact us.",
        }),
        ORDER_SUBMIT_ERROR: intl.formatMessage({
            id: 'place_order.message.error_placing_order.ORDER_SUBMIT_ERROR',
            defaultMessage:
                "We're sorry that your order could not be placed. This may be a system error, please wait a few minutes and resubmit your order. If you are still experiencing this error, please contact us.",
        }),
        DEFAULT: message,
    })[message] ?? (defaultEmpty ? '' : message);

interface PlaceOrderRequest {
    kioskLocationId?: string | null;
    fromPayPal?: boolean;
}

export interface PlaceOrderResponse {
    type: 'ERROR' | 'SUCCESS' | 'SFCC_REDIRECT';
    message: string;
    action: string;
    url: string;
}

export const usePlaceOrder = (): UseMutationResult<
    PlaceOrderResponse,
    unknown,
    PlaceOrderRequest
> =>
    useMutation<PlaceOrderResponse, unknown, PlaceOrderRequest>({
        mutationFn: async (data) => {
            const queryParams: HttpParams = [];
            if (data.kioskLocationId) {
                queryParams.push({ 'kiosk-location-id': data.kioskLocationId });
            }
            if (data.fromPayPal) {
                queryParams.push({ fromPayPal: data.fromPayPal });
            }
            return sfccPost<PlaceOrderResponse>('PwaCOPlaceOrder-PlaceOrder', {}, queryParams);
        },
    });
